"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, variantConfig, type VariantConfig, type VariantProps } from "../../lib/cva";
import * as React from "react";
import { cn } from "../../lib/utils";
import { Icon } from "./icon";
import { Spinner } from "./spinner";
import type { Omittable } from "../../lib/props";

// An avatar leading an expansion pill is a GLYPH, not a cap-filling face: the
// consumer renders it at the 16px glyph rung and the collapsed face pads it
// to centre in an h×h circle — (h − 16px avatar − 2px border) / 2 — so it
// sits on the same icon line as the svg glyphs in neighbouring buttons. The
// trailing pad relaxes once the pill expands and text follows the avatar.
const BUTTON_BASE =
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-(--button-gap) [--button-gap:--spacing(2)] whitespace-nowrap rounded-full border font-medium text-sm outline-none transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-full pointer-coarse:after:absolute pointer-coarse:after:top-1/2 pointer-coarse:after:left-1/2 pointer-coarse:after:-translate-x-1/2 pointer-coarse:after:-translate-y-1/2 pointer-coarse:after:size-full pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-64 data-loading:select-none data-loading:text-transparent data-loading:[&_svg:not([data-slot=button-loading-indicator])]:invisible has-data-[slot=button-expansion]:transition-[color,background-color,border-color,box-shadow] has-data-[slot=button-expansion]:has-[[data-slot=avatar],[data-slot=entity-avatar]]:ps-[calc((var(--button-h)-18px)/2)] has-data-[slot=button-expansion]:has-[[data-slot=avatar],[data-slot=entity-avatar]]:not-data-expanded:pe-[calc((var(--button-h)-18px)/2)] [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:-mx-0.5 [&_svg]:shrink-0";

const buttonGeometry = cva(BUTTON_BASE, {
  defaultVariants: {
    size: "default",
  },
  variants: {
    size: {
      default:
        "h-8 px-[calc(--spacing(3)-1px)] [--button-h:--spacing(8)] [--button-pad:calc(--spacing(2)-1px)] has-data-[slot=button-expansion]:min-w-8 has-data-[slot=button-expansion]:px-(--button-pad)",
      // The icon rungs are a square MINIMUM, not a fixed square: `h-N
      // min-w-N` with no side padding. A lone glyph or avatar centres in a
      // 1:1 box, and content wider than that box — a stacked avatar group,
      // a glyph beside a count — widens the button instead of overflowing
      // both edges. `size-N` could only do the first, so anything wider was
      // pushed onto a text rung, where the side padding renders a lone
      // glyph as a pill.
      // Every rung publishes its height as `--button-h` so content that
      // scales with the host (a corner `CountSticker`) reads it.
      // An icon rung hosting a `ButtonExpansion` (a glyph that grows a count
      // beside itself) takes side insets of (h − 20px)/2 — 6px on the 32px
      // rung, 10px on the 40px one — so the revealed content stops short of
      // the edge instead of running into it. The glyph's visual inset comes
      // out 3px wider than the count's (the svg carries a 2px negative side
      // margin, the border adds 1px); a digit's own side bearing closes most
      // of that. Collapsed, the content is narrower than the square minimum
      // and the glyph centres exactly as it does without the slot.
      icon: "h-8 min-w-8 [--button-h:--spacing(8)] has-data-[slot=button-expansion]:px-[calc((var(--button-h)-20px)/2)]",
      "icon-lg":
        "h-9 min-w-9 [--button-h:--spacing(9)] has-data-[slot=button-expansion]:px-[calc((var(--button-h)-20px)/2)]",
      "icon-sm":
        "h-7 min-w-7 [--button-h:--spacing(7)] has-data-[slot=button-expansion]:px-[calc((var(--button-h)-20px)/2)]",
      "icon-xl":
        "h-10 min-w-10 [--button-h:--spacing(10)] has-data-[slot=button-expansion]:px-[calc((var(--button-h)-20px)/2)] [&_svg:not([class*='size-'])]:size-4.5",
      "icon-xs":
        "h-6 min-w-6 [--button-h:--spacing(6)] has-data-[slot=button-expansion]:px-[calc((var(--button-h)-20px)/2)] not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-3.5",
      lg: "h-9 px-[calc(--spacing(3.5)-1px)] [--button-h:--spacing(9)] [--button-pad:calc(--spacing(2.5)-1px)] has-data-[slot=button-expansion]:min-w-9 has-data-[slot=button-expansion]:px-(--button-pad)",
      sm: "h-7 [--button-gap:--spacing(1.5)] px-[calc(--spacing(2)-1px)] [--button-h:--spacing(7)] [--button-pad:calc(--spacing(1.5)-1px)] has-data-[slot=button-expansion]:min-w-7 has-data-[slot=button-expansion]:px-(--button-pad)",
      xl: "h-10 px-[calc(--spacing(4)-1px)] [--button-h:--spacing(10)] [--button-pad:calc(--spacing(3)-1px)] has-data-[slot=button-expansion]:min-w-10 has-data-[slot=button-expansion]:px-(--button-pad) [&_svg:not([class*='size-'])]:size-4.5",
      // The collapsed pad must fit the 16px glyph rung in a 1:1 box: 1px
      // border + 5px pad + 12px glyph (16px less its 2px side margins) + 5px
      // pad + 1px border = the 24px height, so a lone glyph rests as a
      // circle and `min-w-6` squares anything narrower. The 7px text pad
      // overshoots that by 4px and renders the resting chip as a stadium.
      xs: "h-6 [--button-gap:--spacing(1.5)] px-[calc(--spacing(2)-1px)] text-xs [--button-h:--spacing(6)] [--button-pad:calc(--spacing(1.5)-1px)] has-data-[slot=button-expansion]:min-w-6 has-data-[slot=button-expansion]:px-(--button-pad) [&_svg:not([class*='size-'])]:size-3.5",
    },
    // Content placement on the main axis. Omit for the centered default;
    // `start` is for a full-width button standing in for a list row, whose
    // icon and label must sit where the real rows' leading content sits.
    justify: {
      start: "justify-start",
    },
    // The button spans its container. The width a button otherwise refuses
    // to have — sizing one with a wrapper or a utility class was the
    // recurring gap this banks.
    fullWidth: {
      true: "w-full",
    },
    shape: {
      // The action-cluster geometry: a button acting ON a rectangle — a list
      // row, a value cell, a menu item — takes the rectangle's corners, so
      // the cluster reads as chrome belonging to that row rather than as
      // free-standing controls floating over it. The `before:` overlay
      // carries the Button's own second radius. Omit for the stadium
      // default. (Toggle mirrors this axis from the opposite base.)
      square: "rounded-md before:rounded-md",
      // The base radius, named. Redundant on its own — its job is to mark
      // the shape as DELIBERATE, so a host that squares its unspecified
      // buttons (a table row, a property row) leaves this one alone. See
      // `data-shape`.
      pill: "rounded-full before:rounded-full",
    },
  },
});

/** Structure: what the button is made of. */
export type ButtonVariant = "solid" | "outline" | "ghost" | "link";
/**
 * Hue. `primary` is the default on `solid`; `neutral` everywhere else. A hue
 * a structure does not carry paints that structure's neutral: `outline`
 * carries all five, `solid` carries `primary`, `neutral` and `destructive`,
 * `ghost` carries `neutral` and `destructive`, `link` carries `neutral`.
 */
export type ButtonColor = "primary" | "neutral" | "destructive" | "warning" | "ai";
/**
 * Resting ink of a `neutral` button. The quiet family — `ghost`, `outline`
 * and the neutral `solid` — rests on the `muted-foreground` rung and lifts
 * to `foreground` under the pointer; the control feel comes from the lift.
 * `foreground` is for a button whose content IS the content: a pane title
 * that opens a menu, a value that edits in place, a real secondary call to
 * action. It rests at full ink so it matches the plain text beside it.
 */
export type ButtonInk = "muted" | "foreground";

const SOLID_PRIMARY =
  "not-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] border-primary bg-primary text-primary-foreground shadow-primary/24 shadow-xs hover:bg-primary/90 data-pressed:bg-primary/90 *:data-[slot=button-loading-indicator]:text-primary-foreground [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none";
const SOLID_DESTRUCTIVE =
  "not-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] border-destructive bg-destructive text-white shadow-destructive/24 shadow-xs hover:bg-destructive/90 data-pressed:bg-destructive/90 *:data-[slot=button-loading-indicator]:text-white [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none";
// Hover rides the wash over the opaque fill (`outline`'s `--button-wash`
// mechanic). Dimming the fill toward the ground is imperceptible on dark
// surfaces, where `secondary` sits one rung above the page; a bare
// `hover:bg-wash-hover` would swap the fill out for the alpha wash and the
// chip would go DARKER. Pressed steps the wash up one more rung.
const SOLID_NEUTRAL =
  "border-transparent bg-secondary text-muted-foreground transition-colors [--button-wash:transparent] bg-linear-to-b from-(--button-wash) to-(--button-wash) hover:[--button-wash:var(--wash-hover)] hover:text-foreground data-pressed:[--button-wash:var(--wash-active)] data-pressed:text-foreground *:data-[slot=button-loading-indicator]:text-muted-foreground";
const SOLID_NEUTRAL_FOREGROUND =
  "border-transparent bg-secondary text-secondary-foreground [--button-wash:transparent] bg-linear-to-b from-(--button-wash) to-(--button-wash) hover:[--button-wash:var(--wash-hover)] data-pressed:[--button-wash:var(--wash-active)] *:data-[slot=button-loading-indicator]:text-secondary-foreground [:active,[data-pressed]]:[--button-wash:var(--wash-active)]";
// The quiet family — `ghost`, `outline` and the neutral `solid` —
// rests on ONE secondary rung: label and glyph both
// `muted-foreground`, with the base's 80% svg dim cancelled so the
// glyph sits on the same rung as its label. Hover and press lift the
// ink to `foreground` alongside the wash. Resting at full ink next to
// a hairline reads as body text that happens to be clickable; the
// control feel comes from the lift, not from dark ink.
const GHOST_NEUTRAL =
  "border-transparent text-muted-foreground hover:text-foreground data-pressed:text-foreground [&_svg:not([class*='opacity-'])]:opacity-100 hover:bg-wash-hover data-pressed:bg-wash-active *:data-[slot=button-loading-indicator]:text-foreground";
// The glyph keeps the base 80% dim, so a trailing chevron sits back as the
// secondary affordance behind content at full ink.
const GHOST_NEUTRAL_FOREGROUND =
  "border-transparent text-foreground hover:bg-wash-hover data-pressed:bg-wash-active *:data-[slot=button-loading-indicator]:text-foreground";
// Destructive ink at rest, and the wash is the destructive tint rather than
// the neutral one, so hover reads as "this is the dangerous one" without
// outline or fill chrome. Disabled drops the red for the plain ghost's ink: a
// control that cannot fire has no danger to signal, and red under the
// disabled dim still reads as red.
const GHOST_DESTRUCTIVE =
  "border-transparent text-destructive-foreground disabled:text-foreground hover:bg-destructive/8 data-pressed:bg-destructive/12 *:data-[slot=button-loading-indicator]:text-destructive-foreground";
const LINK_NEUTRAL =
  "border-transparent text-foreground underline-offset-4 hover:underline data-pressed:underline *:data-[slot=button-loading-indicator]:text-foreground";
// The wash rides a gradient layer OVER the opaque fill (the table-row
// `--row-wash` mechanic) rather than replacing it: a bare `hover:bg-wash-hover`
// swaps the fill for an alpha wash that composites against whatever surface
// is BEHIND the button, and can invert the control against any surface whose
// fill differs from `background`.
const OUTLINE_NEUTRAL =
  "border-input bg-background not-dark:bg-clip-padding text-muted-foreground hover:text-foreground data-pressed:text-foreground [&_svg:not([class*='opacity-'])]:opacity-100 [--button-wash:transparent] bg-linear-to-b from-(--button-wash) to-(--button-wash) hover:[--button-wash:var(--wash-hover)] data-pressed:[--button-wash:var(--wash-active)] *:data-[slot=button-loading-indicator]:text-foreground";
// Identity triggers (the workspace name) rest at full ink on the outline
// chip; the glyph keeps the base 80% dim so a trailing chevron sits back.
const OUTLINE_NEUTRAL_FOREGROUND =
  "border-input bg-background not-dark:bg-clip-padding text-foreground [--button-wash:transparent] bg-linear-to-b from-(--button-wash) to-(--button-wash) hover:[--button-wash:var(--wash-hover)] data-pressed:[--button-wash:var(--wash-active)] *:data-[slot=button-loading-indicator]:text-foreground";
// The ON state of a toolbar toggle chip: the solved accent wash
// (`primary-subtle`) under a soft `brand/32` hairline, with the wash's OWN
// solved pair as ink on label and glyph alike — `brand` clears the PAGE at
// Lc 45 and lands under 40 on the deeper wash. Flat, no shadow, so it reads as
// state, not a CTA. Hover steps the wash toward the brand.
const OUTLINE_PRIMARY =
  "border-brand/32 bg-primary-subtle text-primary-subtle-foreground hover:bg-[color-mix(in_oklab,var(--primary-subtle),var(--brand)_12%)] data-pressed:bg-[color-mix(in_oklab,var(--primary-subtle),var(--brand)_12%)] *:data-[slot=button-loading-indicator]:text-primary-subtle-foreground";
const OUTLINE_DESTRUCTIVE =
  "border-input bg-background not-dark:bg-clip-padding text-destructive-foreground shadow-xs/5 not-disabled:not-active:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/4%)] hover:border-destructive/32 hover:bg-destructive/4 data-pressed:border-destructive/32 data-pressed:bg-destructive/4 *:data-[slot=button-loading-indicator]:text-foreground dark:not-disabled:before:shadow-[0_-1px_--theme(--color-white/2%)] dark:not-disabled:not-active:not-data-pressed:before:shadow-[0_-1px_--theme(--color-white/6%)] [:disabled,:active,[data-pressed]]:shadow-none";
// The primary chip in the warning hue: an action the surface is WAITING ON —
// an unsaved edit, a pending confirmation — a held state, not a CTA.
const OUTLINE_WARNING =
  "border-warning/32 bg-warning/16 text-warning-foreground hover:bg-warning/24 data-pressed:bg-warning/24 *:data-[slot=button-loading-indicator]:text-warning-foreground";
// `outline` wearing the AI affordance ring: at rest a plain outline chip, and
// under the pointer the hairline yields to the `[data-ai-ring]` spectrum band
// — Button emits that attribute for this hue, and the band, its turn, and the
// reduced-motion freeze all live in the ring rule (index.css). The hover
// border hand-off and brand glow are restated HERE as utilities: the ring
// rule sits in the components layer, where the hue's own border and shadow
// utilities would silently win.
const OUTLINE_AI =
  "border-input bg-background not-dark:bg-clip-padding text-muted-foreground hover:text-foreground data-pressed:text-foreground [&_svg:not([class*='opacity-'])]:opacity-100 shadow-xs/5 [--button-wash:transparent] bg-linear-to-b from-(--button-wash) to-(--button-wash) hover:[--button-wash:var(--wash-hover)] data-pressed:[--button-wash:var(--wash-active)] hover:border-transparent data-pressed:border-transparent hover:shadow-[0_0_12px_color-mix(in_oklab,var(--brand)_22%,transparent)] not-disabled:not-active:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/4%)] *:data-[slot=button-loading-indicator]:text-foreground dark:not-disabled:before:shadow-[0_-1px_--theme(--color-white/2%)] dark:not-disabled:not-active:not-data-pressed:before:shadow-[0_-1px_--theme(--color-white/6%)]";

const BUTTON_PAINT: Record<
  ButtonVariant,
  Partial<Record<ButtonColor, string>> & { neutral: string }
> = {
  solid: {
    primary: SOLID_PRIMARY,
    neutral: SOLID_NEUTRAL,
    destructive: SOLID_DESTRUCTIVE,
  },
  outline: {
    neutral: OUTLINE_NEUTRAL,
    primary: OUTLINE_PRIMARY,
    destructive: OUTLINE_DESTRUCTIVE,
    warning: OUTLINE_WARNING,
    ai: OUTLINE_AI,
  },
  ghost: {
    neutral: GHOST_NEUTRAL,
    destructive: GHOST_DESTRUCTIVE,
  },
  link: {
    neutral: LINK_NEUTRAL,
  },
};

const BUTTON_FOREGROUND_INK: Partial<Record<ButtonVariant, string>> = {
  solid: SOLID_NEUTRAL_FOREGROUND,
  outline: OUTLINE_NEUTRAL_FOREGROUND,
  ghost: GHOST_NEUTRAL_FOREGROUND,
};

export type ButtonVariantsOptions = Omit<
  VariantProps<typeof buttonGeometry>,
  "className"
> & {
  variant?: ButtonVariant | null | undefined;
  color?: ButtonColor | null | undefined;
  ink?: ButtonInk | null | undefined;
  className?: string | undefined;
};

/** The hue a structure paints when the call site names none. */
export function buttonDefaultColor(variant: ButtonVariant): ButtonColor {
  return variant === "solid" ? "primary" : "neutral";
}

function buttonPaint(
  variant: ButtonVariant,
  color: ButtonColor,
  ink: ButtonInk,
): string {
  const paint = BUTTON_PAINT[variant];
  if (color === "neutral" && ink === "foreground") {
    return BUTTON_FOREGROUND_INK[variant] ?? paint.neutral;
  }
  return paint[color] ?? paint.neutral;
}

/**
 * Button's classes: geometry (size, shape, justify, fullWidth) plus the paint
 * for one structure × hue × ink. A consumer that borrows Button's look for a
 * non-button element (a breadcrumb anchor, a chip on a Box) composes through
 * this, never through the class strings.
 */
export const buttonVariants: ((options?: ButtonVariantsOptions) => string) & {
  config: VariantConfig;
} = Object.assign(
  ({ variant, color, ink, className, ...geometry }: ButtonVariantsOptions = {}) => {
    const structure = variant ?? "solid";
    // The whole string goes through tailwind-merge, so a consumer's
    // `className` wins over the base (`shrink` over `shrink-0`, a `min-w-0`
    // over a geometry width): cva only concatenates.
    return cn(
      buttonGeometry({
        ...geometry,
        className: buttonPaint(structure, color ?? buttonDefaultColor(structure), ink ?? "muted"),
      }),
      className,
    );
  },
  {
    // The docs read a composer's axes from `.config`; the paint axes are
    // listed beside the geometry ones so the matrix enumerates them too.
    config: {
      defaultVariants: { ...variantConfig(buttonGeometry)?.defaultVariants, variant: "solid" },
      variants: {
        ...variantConfig(buttonGeometry)?.variants,
        variant: { solid: "", outline: "", ghost: "", link: "" },
        color: { primary: "", neutral: "", destructive: "", warning: "", ai: "" },
        ink: { muted: "", foreground: "" },
      },
    },
  },
);

// ButtonExpansion needs the expanded state for the a11y tree (`aria-hidden`
// cannot be driven by CSS); everything visual keys off the root's
// `data-expanded` instead.
const ButtonExpandedContext = React.createContext(false);

export type ButtonSize = NonNullable<VariantProps<typeof buttonGeometry>["size"]>;
export type ButtonShape = NonNullable<VariantProps<typeof buttonGeometry>["shape"]>;

export type ButtonProps = useRender.ComponentProps<"button"> &
  Omittable<{
  /** Structure. @default "solid" */
  variant?: ButtonVariant;
  /** Hue. @default "primary" on `solid`, "neutral" on every other structure */
  color?: ButtonColor;
  /** Resting ink of a `neutral` button. @default "muted" */
  ink?: ButtonInk;
  size?: ButtonSize;
  shape?: ButtonShape;
  justify?: VariantProps<typeof buttonGeometry>["justify"];
  fullWidth?: boolean;
  loading?: boolean;
  /** Reveals any `ButtonExpansion` child. Emitted as `data-expanded` on the
   *  root. */
  expanded?: boolean;
}>;

export function Button({
  className,
  variant = "solid",
  color,
  ink,
  size,
  shape,
  justify,
  fullWidth,
  render,
  children,
  loading = false,
  expanded = false,
  disabled: disabledProp,
  ...props
}: ButtonProps): React.ReactElement {
  const isDisabled: boolean = Boolean(loading || disabledProp);
  const typeValue: React.ButtonHTMLAttributes<HTMLButtonElement>["type"] =
    render ? undefined : "button";

  const defaultProps = {
    children: (
      <>
        {children}
        {/* Explicit md (16px) — the Spinner emits its own size-* class, so
            the button's descendant-svg pin no longer applies to it. */}
        <Spinner
          className="pointer-events-none absolute hidden in-data-loading:block"
          data-slot="button-loading-indicator"
          size="md"
        />
      </>
    ),
    className: buttonVariants({ className, color, fullWidth, ink, justify, shape, size, variant }),
    "aria-disabled": loading || undefined,
    // The `ai` hue's hover spectrum band — the [data-ai-ring] rule owns the
    // ::after band and its motion; the hue's classes own the resting chrome
    // and the hover hand-off.
    "data-ai-ring": variant === "outline" && color === "ai" ? "" : undefined,
    "data-expanded": expanded ? "" : undefined,
    "data-loading": loading ? "" : undefined,
    // Always present: the named shape, or `default` when the call site left
    // it to the base. A host that squares its unspecified buttons selects
    // `[data-shape=default]`, so passing any `shape` — including `pill` —
    // opts that button out of the host's default and hands the geometry back
    // to the call site. It is its own attribute rather than a `data-slot`
    // read because a Base UI trigger part rendered into the Button (Popover,
    // Tooltip, Menu) replaces `data-slot` with its own; nothing else writes
    // `data-shape`, so it survives every render chain.
    "data-shape": shape ?? "default",
    "data-slot": "button",
    disabled: isDisabled,
    type: typeValue,
  };

  return (
    <ButtonExpandedContext.Provider value={expanded}>
      {useRender({
        defaultTagName: "button",
        props: mergeProps<"button">(defaultProps, props),
        render,
      })}
    </ButtonExpandedContext.Provider>
  );
}

export type ButtonExpansionProps = useRender.ComponentProps<"span">;

/**
 * Content that slides open inside a Button when its `expanded` prop is set —
 * a revealed label, a count badge, a `ButtonClear`. Always in the DOM;
 * collapsed it is zero-width, faded, and `aria-hidden`, so expanding is a
 * pure CSS width interpolation (`grid-template-columns: 0fr → 1fr`) with no
 * measurement and no remount.
 */
export function ButtonExpansion({
  className,
  render,
  children,
  ...props
}: ButtonExpansionProps): React.ReactElement {
  const expanded = React.useContext(ButtonExpandedContext);

  // The collapse must be able to animate content the caller unmounts at the
  // same moment it stops being expanded (a grouping set back to none) — an
  // empty track has no width to interpolate from. Holding the last non-null
  // children keeps the outgoing content painted while the slot slides shut.
  const heldChildren = React.useRef(children);
  if (children != null) heldChildren.current = children;

  const defaultProps = {
    "aria-hidden": expanded ? undefined : true,
    children: (
      <span className="flex min-w-0 items-center gap-(--button-gap) overflow-hidden whitespace-nowrap">
        {children ?? heldChildren.current}
      </span>
    ),
    className: cn(
      "pointer-events-none grid [grid-template-columns:0fr] opacity-0 -ml-(--button-gap) transition-[grid-template-columns,opacity,margin] duration-fast ease-button in-data-expanded:pointer-events-auto in-data-expanded:[grid-template-columns:1fr] in-data-expanded:opacity-100 in-data-expanded:ml-0",
      className,
    ),
    "data-slot": "button-expansion",
  };

  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(defaultProps, props),
    render,
  });
}

export type ButtonClearProps = useRender.ComponentProps<"span">;

/**
 * The clear (×) affordance for a toggle-style Button, composed as a direct
 * child (never inside `ButtonExpansion` — it must escape the slot's clip).
 * It overlays the trailing cap as the same nestled circle as a cap-filling
 * face — absolutely positioned, so it adds no width to the button — and
 * reveals only while the button is hovered and expanded. A real button cannot
 * nest inside a button, so it is a click target on a span that stops
 * propagation on the whole press sequence — clearing never also presses the
 * button, nor opens the overlay when the button it caps is a Menu/Popover/
 * Select trigger. Children default to the × glyph.
 *
 * Pointer-only, and `aria-hidden` for that reason: a hover-revealed target
 * inside a button is unreachable by keyboard, and ARIA forbids an interactive
 * descendant of a button, so a `role="button"` here would announce a control
 * nobody can operate. The host button carries the clearing act for everyone
 * else — same rule as {@link CountSticker}.
 */
export function ButtonClear({
  className,
  render,
  children,
  ...props
}: ButtonClearProps): React.ReactElement {
  const defaultProps = {
    "aria-hidden": true,
    // `opacity-100` opts the glyph out of Button's 80% dim on class-less
    // svgs — at the disc's size a faded × stops reading as a control.
    children: children ?? <Icon name="x" className="opacity-100" />,
    className: cn(
      // The disc is the solid accent — `brand` fill with the × punched out
      // in `background` ink. The pair contrasts by construction (`brand` is
      // defined as accent ink readable against `background`, so the
      // inversion is too), where a brand-tinted disc under a brand × goes
      // tone-on-tone and the glyph melts. The resting fill sits a step
      // below full brand so the disc reads level with, not brighter than,
      // a solid accent at the chip's other cap (dark-mode `brand` is the
      // brightest accent token); direct hover strengthens it. Both fills
      // are pre-mixed, never an alpha — the disc covers the clipped label,
      // so it must stay opaque.
      "pointer-events-none absolute end-px top-1/2 flex size-[calc(var(--button-h)-4px)] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--brand)_86%,var(--background))] text-background opacity-0 transition-opacity duration-fast hover:bg-[color-mix(in_oklab,var(--brand)_94%,var(--background))] [[data-expanded]:hover_&]:pointer-events-auto [[data-expanded]:hover_&]:opacity-100",
      className,
    ),
    "data-slot": "button-clear",
    onClick: (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
    },
    // Overlay triggers (Base UI Menu/Popover/Select) open on POINTERDOWN, not
    // click, so stopping the click alone still opens the popup on the way out
    // — clearing a filter chip that is also a menu trigger would clear AND
    // open. Both spellings are needed: pointerdown for the overlay parts and
    // mousedown for anything still listening to the legacy pair.
    onPointerDown: (event: React.PointerEvent<HTMLSpanElement>) => {
      event.stopPropagation();
    },
    onMouseDown: (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
    },
  };

  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(defaultProps, props),
    render,
  });
}

export type ButtonGroupProps = useRender.ComponentProps<"div">;

/**
 * Buttons joined into one control: a primary action with its own menu, a
 * segmented pair.
 *
 * The children keep their own variant, size and shape — the group only
 * squares the corners they SHARE and pulls the seam onto a single border, so
 * the pair reads as one shape instead of two controls that happen to touch.
 * `before:` follows every radius because that overlay carries the Button's
 * second radius. The hovered or focused segment is raised so its ring is not
 * clipped by the neighbour overlapping it.
 *
 * The seam selectors count only REAL segments: while a popup trigger in the
 * group is open, Base UI mounts visually-hidden focus-guard spans
 * (`data-base-ui-focus-guard`) as siblings before and after it, so a plain
 * `:first-child`/`:last-child` would square the outer corners of an
 * edge-segment trigger for exactly as long as its menu is open. "Follows a
 * real segment" (`real ~ real`) and "a real segment follows" (`:has(~ real)`)
 * are guard-blind spellings of "not first" and "not last".
 */
export function ButtonGroup({
  className,
  render,
  ...props
}: ButtonGroupProps): React.ReactElement {
  const defaultProps = {
    className: cn(
      "inline-flex items-stretch",
      "[&>*:not([data-base-ui-focus-guard])~*:not([data-base-ui-focus-guard])]:-ms-px [&>*:not([data-base-ui-focus-guard])~*:not([data-base-ui-focus-guard])]:rounded-s-none [&>*:not([data-base-ui-focus-guard])~*:not([data-base-ui-focus-guard])]:before:rounded-s-none",
      "[&>*:not([data-base-ui-focus-guard]):has(~*:not([data-base-ui-focus-guard]))]:rounded-e-none [&>*:not([data-base-ui-focus-guard]):has(~*:not([data-base-ui-focus-guard]))]:before:rounded-e-none",
      "[&>*:hover]:z-10 [&>*:focus-visible]:z-10",
      className,
    ),
    "data-slot": "button-group",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}
